import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, Project, Milestone, MilestoneStatus } from '../types';
import { listProjects } from '../services/projectService';
import { getAllMilestones } from '../services/milestoneService';
import {
  listAllProjectDependencies, createProjectDependency, deleteProjectDependency,
  ProjectDependency, ProjectDependencyType,
} from '../services/projectDependencyService';
import { isPortfolioManagerOrAbove } from '../services/portfolioRoleService';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../components/BoardModal';

const DEP_TYPE_LABEL: Record<number, string> = {
  [ProjectDependencyType.FinishToStart]: 'Finish → Start',
  [ProjectDependencyType.StartToStart]: 'Start → Start',
  [ProjectDependencyType.FinishToFinish]: 'Finish → Finish',
};

interface Props { pca: IPublicClientApplication; theme: AppTheme }

const DAY_MS = 86400000;
const ROW_H = 44;
const HEADER_H = 32;
const LEFT_PANEL_W = 220;
const PX_PER_DAY_OPTIONS = { week: 24, month: 6, quarter: 2 } as const;
type ZoomLevel = keyof typeof PX_PER_DAY_OPTIONS;

export default function PortfolioGantt({ pca }: Props) {
  const nav = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [deps, setDeps] = useState<ProjectDependency[]>([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState<ZoomLevel>('month');
  const [showAddDep, setShowAddDep] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  // When a milestone is clicked (by an authorized user) we open the modal
  // seeded with that milestone as the predecessor.
  const [seed, setSeed] = useState<{ projectId: string; milestoneId: string } | null>(null);

  useEffect(() => {
    isPortfolioManagerOrAbove(pca).then(setCanCreate).catch(() => setCanCreate(false));
  }, [pca]);

  const openAddFromMilestone = useCallback((projectId: string, milestoneId: string) => {
    setSeed({ projectId, milestoneId });
    setShowAddDep(true);
  }, []);

  const reloadDeps = useCallback(async () => {
    setDeps(await listAllProjectDependencies(pca));
  }, [pca]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, d] = await Promise.all([
        listProjects(pca),
        getAllMilestones(pca),
        listAllProjectDependencies(pca),
      ]);
      setProjects(p);
      setMilestones(m.filter(ms => ms.dagrun_pm_duedate));
      setDeps(d);
    } finally { setLoading(false); }
  }, [pca]);
  useEffect(() => { reload(); }, [reload]);

  const dayPx = PX_PER_DAY_OPTIONS[zoom];

  // Compute chart extents from milestones AND project rows so projects with
  // no milestones can still occupy a default bar window centred around today.
  const { minMs, totalDays, timelineW } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    milestones.forEach(m => {
      if (!m.dagrun_pm_duedate) return;
      const t = new Date(m.dagrun_pm_duedate).getTime();
      if (t < min) min = t; if (t > max) max = t;
    });
    if (!isFinite(min)) { min = Date.now() - 30 * DAY_MS; max = Date.now() + 90 * DAY_MS; }
    min -= 7 * DAY_MS;
    max += 14 * DAY_MS;
    const totalDays = Math.max(30, Math.ceil((max - min) / DAY_MS));
    return { minMs: min, totalDays, timelineW: totalDays * dayPx };
  }, [milestones, dayPx]);

  // Per-project bar span: from earliest milestone to latest milestone, falling
  // back to a 30-day window centred at today when the project has no
  // milestones. Returns null for projects we can't place at all (which
  // shouldn't happen given the fallback).
  const projectSpan = (projectId: string): { start: number; end: number; isFallback: boolean } | null => {
    const ms = projectMilestones.get(projectId) ?? [];
    let s = Infinity, e = -Infinity;
    ms.forEach(m => {
      if (!m.dagrun_pm_duedate) return;
      const t = new Date(m.dagrun_pm_duedate).getTime();
      if (t < s) s = t; if (t > e) e = t;
    });
    if (!isFinite(s)) {
      const today = Date.now();
      return { start: today - 7 * DAY_MS, end: today + 23 * DAY_MS, isFallback: true };
    }
    if (s === e) e = s + 7 * DAY_MS;
    return { start: s, end: e, isFallback: false };
  };

  // Group milestones by project
  const projectMilestones = useMemo(() => {
    const by = new Map<string, Milestone[]>();
    milestones.forEach(m => {
      const pid = m._dagrun_pm_projectid_value;
      if (!pid) return;
      const arr = by.get(pid) ?? [];
      arr.push(m);
      by.set(pid, arr);
    });
    return by;
  }, [milestones]);

  const milestoneById = useMemo(() => {
    const m = new Map<string, Milestone>();
    milestones.forEach(x => m.set(x.dagrun_pm_milestoneid, x));
    return m;
  }, [milestones]);

  // Show every project — projects without milestones get a fallback 30-day
  // bar centred at today so they're visible in the timeline.
  const visibleProjects = useMemo(() => projects, [projects]);

  const projectRowIndex = useMemo(() => {
    const idx = new Map<string, number>();
    visibleProjects.forEach((p, i) => idx.set(p.dagrun_pm_projectid, i));
    return idx;
  }, [visibleProjects]);

  const xFor = (iso: string) => ((new Date(iso).getTime() - minMs) / DAY_MS) * dayPx;

  // Determine slip for a dependency: if pred due > succ start, that's a slip.
  const isSlip = (dep: ProjectDependency): boolean => {
    if (dep.dagrun_pm_slipdetected) return true;
    const pred = dep.dagrun_pm_predecessormilestoneid ? milestoneById.get(dep.dagrun_pm_predecessormilestoneid) : null;
    const succ = dep.dagrun_pm_successormilestoneid   ? milestoneById.get(dep.dagrun_pm_successormilestoneid)   : null;
    if (!pred?.dagrun_pm_duedate || !succ?.dagrun_pm_duedate) return false;
    const predDue = new Date(pred.dagrun_pm_duedate).getTime();
    const succDue = new Date(succ.dagrun_pm_duedate).getTime();
    const lag = (dep.dagrun_pm_lagdays ?? 0) * DAY_MS;
    return predDue + lag > succDue;
  };

  const todayX = ((Date.now() - minMs) / DAY_MS) * dayPx;
  const todayVisible = todayX >= 0 && todayX <= timelineW;

  return (
    <div className="page" style={{ paddingBottom: 40 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h2 style={{ marginBottom: 0 }}>Portfolio</h2>
          <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)' }}>
            <button
              type="button"
              onClick={() => nav('/portfolio/full')}
              style={{
                border: 'none', background: 'transparent',
                color: 'var(--ink-3)',
                fontWeight: 400, fontFamily: 'var(--font-ui)', fontSize: 12,
                padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
              }}
            >Overview</button>
            <button
              type="button"
              style={{
                border: 'none', background: 'var(--brand-mist)', color: 'var(--brand)',
                fontWeight: 500, fontFamily: 'var(--font-ui)', fontSize: 12,
                padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
              }}
            >Timeline</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 5 }}>
            {(Object.keys(PX_PER_DAY_OPTIONS) as ZoomLevel[]).map(z => (
              <button key={z} type="button" onClick={() => setZoom(z)}
                style={{
                  border: 'none',
                  background: zoom === z ? 'var(--brand-mist)' : 'transparent',
                  color: zoom === z ? 'var(--brand)' : 'var(--ink-3)',
                  fontWeight: zoom === z ? 500 : 400,
                  fontSize: 12, padding: '4px 10px',
                  borderRadius: 4, cursor: 'pointer', textTransform: 'capitalize',
                }}
              >{z}</button>
            ))}
          </div>
          {canCreate && (
            <button type="button" onClick={() => { setSeed(null); setShowAddDep(true); }}
              style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >+ Add Dependency</button>
          )}
        </div>
      </div>

      {loading && <div className="spinner" style={{ margin: '80px auto' }} />}
      {!loading && visibleProjects.length === 0 && (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
          No projects with milestones to display on the portfolio timeline.
        </div>
      )}
      {!loading && visibleProjects.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', overflow: 'auto' }}>
          <div style={{ display: 'flex', minWidth: LEFT_PANEL_W + 400 }}>
            {/* Left project list */}
            <div style={{ flexShrink: 0, width: LEFT_PANEL_W, borderRight: '0.5px solid var(--border-light)' }}>
              <div style={{
                padding: '0 12px', fontWeight: 600, fontSize: 12, color: 'var(--ink-2)',
                borderBottom: '0.5px solid var(--border-light)',
                background: 'var(--surface-2)',
                height: HEADER_H, display: 'flex', alignItems: 'center',
              }}>Project</div>
              {visibleProjects.map(p => (
                <div key={p.dagrun_pm_projectid}
                  onClick={() => nav(`/project/${p.dagrun_pm_projectid}`)}
                  style={{
                    padding: '0 12px', height: ROW_H,
                    borderBottom: '0.5px solid var(--border-light)',
                    cursor: 'pointer', fontSize: 13, color: 'var(--ink)',
                    display: 'flex', alignItems: 'center',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >{p.dagrun_pm_name}</div>
              ))}
            </div>

            {/* Right timeline */}
            <div style={{ flex: 1, position: 'relative', overflowX: 'auto' }}>
              <TimelineHeader zoom={zoom} minMs={minMs} totalDays={totalDays} dayPx={dayPx}
                width={timelineW} todayX={todayVisible ? todayX : null} />
              <div style={{ position: 'relative', width: timelineW, height: visibleProjects.length * ROW_H }}>
                {/* Row backgrounds */}
                {visibleProjects.map((_, i) => (
                  <div key={`bg-${i}`} style={{
                    position: 'absolute', left: 0, right: 0,
                    top: i * ROW_H, height: ROW_H,
                    borderBottom: '0.5px solid var(--border-light)',
                    background: i % 2 === 0 ? 'transparent' : 'rgba(245,246,248,0.4)',
                  }} />
                ))}

                {/* Dependency arrows (SVG overlay, dashed) */}
                <svg style={{
                  position: 'absolute', left: 0, top: 0,
                  width: timelineW, height: visibleProjects.length * ROW_H,
                  pointerEvents: 'none', overflow: 'visible',
                }}>
                  <defs>
                    <marker id="pg-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M0,0 L10,5 L0,10 Z" fill="var(--ink-4)" />
                    </marker>
                    <marker id="pg-arrow-slip" viewBox="0 0 10 10" refX="8" refY="5"
                            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M0,0 L10,5 L0,10 Z" fill="#DC2626" />
                    </marker>
                  </defs>
                  {deps.map(dep => {
                    const pred = dep.dagrun_pm_predecessormilestoneid ? milestoneById.get(dep.dagrun_pm_predecessormilestoneid) : null;
                    const succ = dep.dagrun_pm_successormilestoneid   ? milestoneById.get(dep.dagrun_pm_successormilestoneid)   : null;
                    if (!pred?.dagrun_pm_duedate || !succ?.dagrun_pm_duedate) return null;
                    const pRow = pred._dagrun_pm_projectid_value ? projectRowIndex.get(pred._dagrun_pm_projectid_value) : undefined;
                    const sRow = succ._dagrun_pm_projectid_value ? projectRowIndex.get(succ._dagrun_pm_projectid_value) : undefined;
                    if (pRow == null || sRow == null) return null;

                    const x1 = xFor(pred.dagrun_pm_duedate);
                    const y1 = pRow * ROW_H + ROW_H / 2;
                    const x2 = xFor(succ.dagrun_pm_duedate);
                    const y2 = sRow * ROW_H + ROW_H / 2;

                    // Skip if both endpoints are off-screen on the same side.
                    if ((x1 < 0 && x2 < 0) || (x1 > timelineW && x2 > timelineW)) return null;

                    const slip = isSlip(dep);
                    // L-shape: horizontal segment from pred to midpoint x, then vertical, then horizontal to succ.
                    const midX = Math.max(x1 + 10, Math.min(x2 - 10, (x1 + x2) / 2));
                    const d = `M${x1},${y1} L${midX},${y1} L${midX},${y2} L${x2},${y2}`;
                    const predProj = pred._dagrun_pm_projectid_value ? projects.find(p => p.dagrun_pm_projectid === pred._dagrun_pm_projectid_value)?.dagrun_pm_name : null;
                    const succProj = succ._dagrun_pm_projectid_value ? projects.find(p => p.dagrun_pm_projectid === succ._dagrun_pm_projectid_value)?.dagrun_pm_name : null;
                    const lag = dep.dagrun_pm_lagdays ?? 0;
                    const hover =
                      `${dep.dagrun_pm_name}\n` +
                      `${DEP_TYPE_LABEL[dep.dagrun_pm_dependencytype ?? ProjectDependencyType.FinishToStart] ?? 'Finish → Start'}` +
                      (lag ? ` · lag ${lag}d` : '') + '\n' +
                      `From: ${predProj ?? '—'} — ${pred.dagrun_pm_name}${pred.dagrun_pm_duedate ? ` (${new Date(pred.dagrun_pm_duedate).toLocaleDateString()})` : ''}\n` +
                      `To: ${succProj ?? '—'} — ${succ.dagrun_pm_name}${succ.dagrun_pm_duedate ? ` (${new Date(succ.dagrun_pm_duedate).toLocaleDateString()})` : ''}` +
                      (slip ? '\n⚠ Slip detected — predecessor finishes after successor' : '');
                    return (
                      <g key={dep.dagrun_pm_projectdependencyid}>
                        <title>{hover}</title>
                        <path d={d} fill="none"
                          stroke={slip ? '#DC2626' : 'var(--ink-4)'}
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          markerEnd={slip ? 'url(#pg-arrow-slip)' : 'url(#pg-arrow)'}
                        />
                      </g>
                    );
                  })}
                </svg>

                {/* Project span bars — one per row, click to open project. */}
                {visibleProjects.map((p, rowIdx) => {
                  const span = projectSpan(p.dagrun_pm_projectid);
                  if (!span) return null;
                  const left = ((span.start - minMs) / DAY_MS) * dayPx;
                  const right = ((span.end - minMs) / DAY_MS) * dayPx;
                  const width = Math.max(8, right - left);
                  const top = rowIdx * ROW_H + (ROW_H - 14) / 2;
                  return (
                    <div
                      key={`bar-${p.dagrun_pm_projectid}`}
                      onClick={() => nav(`/project/${p.dagrun_pm_projectid}`)}
                      title={`${p.dagrun_pm_name}${span.isFallback ? ' (no milestones — placeholder span)' : ''}`}
                      style={{
                        position: 'absolute',
                        left, top, width, height: 14,
                        background: span.isFallback ? 'var(--ink-5)' : 'var(--brand)',
                        opacity: span.isFallback ? 0.45 : 0.85,
                        borderRadius: 3,
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    />
                  );
                })}

                {/* Milestone diamonds per project */}
                {visibleProjects.map((p, rowIdx) => {
                  const ms = projectMilestones.get(p.dagrun_pm_projectid) ?? [];
                  return ms.map(m => {
                    if (!m.dagrun_pm_duedate) return null;
                    const cx = xFor(m.dagrun_pm_duedate);
                    const cy = rowIdx * ROW_H + ROW_H / 2;
                    const complete = m.dagrun_pm_status === MilestoneStatus.Complete;
                    return (
                      <svg key={m.dagrun_pm_milestoneid}
                        width="16" height="16"
                        style={{ position: 'absolute', left: cx - 8, top: cy - 8, overflow: 'visible', cursor: 'pointer' }}
                        onClick={() => canCreate
                          ? openAddFromMilestone(p.dagrun_pm_projectid, m.dagrun_pm_milestoneid)
                          : nav(`/project/${p.dagrun_pm_projectid}`)}
                      >
                        <title>{`${p.dagrun_pm_name} — ${m.dagrun_pm_name} — ${new Date(m.dagrun_pm_duedate).toLocaleDateString()}`
                          + (canCreate ? '\nClick to add a cross-project dependency from this milestone' : '')}</title>
                        <rect x={2} y={2} width={12} height={12}
                          transform="rotate(45 8 8)"
                          fill={complete ? 'var(--brand)' : 'var(--surface)'}
                          stroke="var(--brand)" strokeWidth={1.5}
                        />
                      </svg>
                    );
                  });
                })}

                {/* Today line */}
                {todayVisible && (
                  <div style={{
                    position: 'absolute',
                    left: todayX - 0.75, top: 0,
                    width: 1.5, height: visibleProjects.length * ROW_H,
                    background: 'var(--brand)', opacity: 0.7,
                    pointerEvents: 'none',
                  }} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddDep && (
        <AddDependencyModal
          pca={pca}
          projects={visibleProjects.length > 0 ? visibleProjects : projects}
          milestonesByProject={projectMilestones}
          allMilestones={milestoneById}
          initialPredProjectId={seed?.projectId}
          initialPredMilestoneId={seed?.milestoneId}
          onClose={() => { setShowAddDep(false); setSeed(null); }}
          onCreated={() => { setShowAddDep(false); setSeed(null); reloadDeps(); }}
        />
      )}
    </div>
  );
}

function TimelineHeader({ zoom, minMs, totalDays, dayPx, width, todayX }: {
  zoom: ZoomLevel; minMs: number; totalDays: number; dayPx: number; width: number; todayX: number | null;
}) {
  const ticks: { x: number; label: string; strong?: boolean }[] = [];
  if (zoom === 'week') {
    for (let i = 0; i < totalDays; i += 7) {
      const d = new Date(minMs + i * DAY_MS);
      ticks.push({
        x: i * dayPx,
        label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        strong: true,
      });
    }
  } else if (zoom === 'month') {
    const start = new Date(minMs);
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    if (cursor.getTime() < minMs) cursor = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    while (cursor.getTime() <= minMs + totalDays * DAY_MS) {
      ticks.push({
        x: ((cursor.getTime() - minMs) / DAY_MS) * dayPx,
        label: cursor.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        strong: true,
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
  } else {
    // quarter
    const start = new Date(minMs);
    const qMonth = Math.floor(start.getMonth() / 3) * 3;
    let cursor = new Date(start.getFullYear(), qMonth, 1);
    while (cursor.getTime() <= minMs + totalDays * DAY_MS) {
      const q = Math.floor(cursor.getMonth() / 3) + 1;
      ticks.push({
        x: ((cursor.getTime() - minMs) / DAY_MS) * dayPx,
        label: `Q${q} ${cursor.getFullYear()}`,
        strong: true,
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 1);
    }
  }
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 5,
      width, height: HEADER_H,
      background: 'var(--surface-2)',
      borderBottom: '0.5px solid var(--border-light)',
    }}>
      {ticks.map((t, i) => (
        <div key={i} style={{
          position: 'absolute', left: t.x, top: 0, height: '100%',
          borderLeft: '0.5px solid var(--border)',
          paddingLeft: 4, paddingRight: 4,
          fontSize: 11, color: 'var(--ink-4)',
          fontFamily: 'var(--font-mono)',
          display: 'flex', alignItems: 'center',
          whiteSpace: 'nowrap',
        }}>{t.label}</div>
      ))}
      {todayX != null && (
        <div style={{
          position: 'absolute', left: todayX, top: 2,
          transform: 'translateX(-50%)',
          fontSize: 10, fontWeight: 600,
          color: 'var(--brand)', background: 'var(--surface)',
          padding: '0 4px', borderRadius: 'var(--r-sm)',
          pointerEvents: 'none',
        }}>Today</div>
      )}
    </div>
  );
}

function AddDependencyModal({ pca, projects, milestonesByProject, allMilestones, initialPredProjectId, initialPredMilestoneId, onClose, onCreated }: {
  pca: IPublicClientApplication;
  projects: Project[];
  milestonesByProject: Map<string, Milestone[]>;
  allMilestones: Map<string, Milestone>;
  initialPredProjectId?: string;
  initialPredMilestoneId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [predProjectId, setPredProjectId] = useState(initialPredProjectId ?? '');
  const [predMilestoneId, setPredMilestoneId] = useState(initialPredMilestoneId ?? '');
  const [succProjectId, setSuccProjectId] = useState('');
  const [succMilestoneId, setSuccMilestoneId] = useState('');
  const [lagDays, setLagDays] = useState('0');
  const [depType, setDepType] = useState<number>(ProjectDependencyType.FinishToStart);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset milestone when project changes — but skip the initial mount so a
  // seeded predecessor (from clicking a milestone) survives.
  const predMounted = useRef(false);
  const succMounted = useRef(false);
  useEffect(() => {
    if (!predMounted.current) { predMounted.current = true; return; }
    setPredMilestoneId('');
  }, [predProjectId]);
  useEffect(() => {
    if (!succMounted.current) { succMounted.current = true; return; }
    setSuccMilestoneId('');
  }, [succProjectId]);

  const predMilestones = predProjectId ? milestonesByProject.get(predProjectId) ?? [] : [];
  const succMilestones = succProjectId ? milestonesByProject.get(succProjectId) ?? [] : [];

  const submit = async () => {
    if (!name.trim() || !predProjectId || !predMilestoneId || !succProjectId || !succMilestoneId || saving) return;
    if (predMilestoneId === succMilestoneId) {
      setError('Predecessor and successor must be different milestones.');
      return;
    }
    setSaving(true); setError(null);
    try {
      // Compute slipDetected at save time.
      const pred = allMilestones.get(predMilestoneId);
      const succ = allMilestones.get(succMilestoneId);
      let slip = false;
      if (pred?.dagrun_pm_duedate && succ?.dagrun_pm_duedate) {
        const predDue = new Date(pred.dagrun_pm_duedate).getTime();
        const succDue = new Date(succ.dagrun_pm_duedate).getTime();
        const lag = (parseInt(lagDays, 10) || 0) * DAY_MS;
        slip = predDue + lag > succDue;
      }
      await createProjectDependency(pca, {
        name: name.trim(),
        predecessorProjectId: predProjectId,
        predecessorMilestoneId: predMilestoneId,
        successorProjectId: succProjectId,
        successorMilestoneId: succMilestoneId,
        lagDays: parseInt(lagDays, 10) || 0,
        dependencyType: depType,
        slipDetected: slip,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Cross-Project Dependency" onClose={onClose} width={600}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit}
          disabled={saving || !name.trim() || !predProjectId || !predMilestoneId || !succProjectId || !succMilestoneId}>
          {saving ? 'Creating…' : 'Add Dependency'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. Data migration precedes UAT kickoff" />
      </div>

      <div style={{ padding: 12, background: '#F5F6F8', borderRadius: 6, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Predecessor
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={BM_LABEL}>Project</label>
          <select value={predProjectId} onChange={e => setPredProjectId(e.target.value)} style={BM_INPUT}>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.dagrun_pm_projectid} value={p.dagrun_pm_projectid}>{p.dagrun_pm_name}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Milestone</label>
          <select value={predMilestoneId} onChange={e => setPredMilestoneId(e.target.value)} style={BM_INPUT} disabled={!predProjectId}>
            <option value="">— Select milestone —</option>
            {predMilestones.map(m => (
              <option key={m.dagrun_pm_milestoneid} value={m.dagrun_pm_milestoneid}>
                {m.dagrun_pm_name} {m.dagrun_pm_duedate ? `(${new Date(m.dagrun_pm_duedate).toLocaleDateString()})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ padding: 12, background: '#F5F6F8', borderRadius: 6, marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Successor
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={BM_LABEL}>Project</label>
          <select value={succProjectId} onChange={e => setSuccProjectId(e.target.value)} style={BM_INPUT}>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.dagrun_pm_projectid} value={p.dagrun_pm_projectid}>{p.dagrun_pm_name}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Milestone</label>
          <select value={succMilestoneId} onChange={e => setSuccMilestoneId(e.target.value)} style={BM_INPUT} disabled={!succProjectId}>
            <option value="">— Select milestone —</option>
            {succMilestones.map(m => (
              <option key={m.dagrun_pm_milestoneid} value={m.dagrun_pm_milestoneid}>
                {m.dagrun_pm_name} {m.dagrun_pm_duedate ? `(${new Date(m.dagrun_pm_duedate).toLocaleDateString()})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={BM_LABEL}>Lag Days</label>
          <input type="number" value={lagDays} onChange={e => setLagDays(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Dependency Type</label>
          <select value={depType} onChange={e => setDepType(Number(e.target.value))} style={BM_INPUT}>
            <option value={ProjectDependencyType.FinishToStart}>Finish to Start</option>
            <option value={ProjectDependencyType.StartToStart}>Start to Start</option>
            <option value={ProjectDependencyType.FinishToFinish}>Finish to Finish</option>
          </select>
        </div>
      </div>

      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// Expose delete to future management UI — referenced from the session report.
export { deleteProjectDependency };
